import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// ─── THEME ───────────────────────────────────────────────────────────────────
const C = {
  navy:     '#1B3A5C',
  navyDark: '#122840',
  navyMid:  '#234870',
  teal:     '#2A9D8F',
  tealDark: '#1F7A6E',
  tealLight:'#3DBFB0',
  white:    '#FFFFFF',
  offWhite: '#F7F9FC',
  slate:    '#64748B',
  slateLight: '#94A3B8',
  border:   '#E2E8F0',
  gold:     '#E9B44C',
  red:      '#DC2626',
};

// ─── FONT INJECTION ───────────────────────────────────────────────────────────
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { font-family: 'DM Sans', sans-serif; color: ${C.navy}; background: ${C.white}; }

    .lp-btn-primary {
      display: inline-flex; align-items: center; gap: 8px;
      background: ${C.teal}; color: ${C.white};
      padding: 16px 32px; border-radius: 4px; font-size: 17px; font-weight: 600;
      border: none; cursor: pointer; text-decoration: none;
      transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
      font-family: 'DM Sans', sans-serif;
      letter-spacing: 0.01em;
    }
    .lp-btn-primary:hover {
      background: ${C.tealDark}; transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(42,157,143,0.35);
    }
    .lp-btn-secondary {
      display: inline-flex; align-items: center; gap: 8px;
      background: transparent; color: ${C.white};
      padding: 15px 31px; border-radius: 4px; font-size: 17px; font-weight: 500;
      border: 1.5px solid rgba(255,255,255,0.4); cursor: pointer; text-decoration: none;
      transition: border-color 0.2s, background 0.2s;
      font-family: 'DM Sans', sans-serif;
    }
    .lp-btn-secondary:hover { border-color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.08); }

    .fade-in { opacity: 0; transform: translateY(24px); transition: opacity 0.65s ease, transform 0.65s ease; }
    .fade-in.visible { opacity: 1; transform: translateY(0); }

    @keyframes pulse-badge { 0%,100% { opacity:1; } 50% { opacity:0.7; } }

    @media (max-width: 768px) {
      .hero-btns { flex-direction: column !important; align-items: stretch !important; }
      .hero-btns a, .hero-btns button { text-align: center; justify-content: center; }
      .steps-grid { grid-template-columns: 1fr !important; }
      .features-grid { grid-template-columns: 1fr !important; }
      .pricing-grid { grid-template-columns: 1fr !important; }
      .hero-headline { font-size: clamp(32px, 6vw, 56px) !important; }
      .stats-row { grid-template-columns: 1fr 1fr !important; }
    }
  `}</style>
);

// ─── FADE-IN HOOK ─────────────────────────────────────────────────────────────
function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ─── SECTION WRAPPER ──────────────────────────────────────────────────────────
const Section: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => {
  const ref = useFadeIn();
  return (
    <div ref={ref} className="fade-in" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', ...style }}>
      {children}
    </div>
  );
};

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

const Navbar = () => (
  <nav style={{
    position: 'sticky', top: 0, zIndex: 100,
    background: C.navyDark, borderBottom: `2px solid ${C.teal}`,
    padding: '0 32px',
  }}>
    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/logo.png" alt="FloridaSIRS" style={{ height: 44 }} />
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.white, letterSpacing: '-0.01em' }}>
          FloridaSIRS<span style={{ color: C.teal }}>.org</span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
        <a href="#how-it-works" style={{ color: C.slateLight, textDecoration: 'none', fontSize: 15, fontWeight: 500 }}>How it works</a>
        <a href="#pricing" style={{ color: C.slateLight, textDecoration: 'none', fontSize: 15, fontWeight: 500 }}>Pricing</a>
        <Link to="/blog" style={{ color: C.slateLight, textDecoration: 'none', fontSize: 15, fontWeight: 500 }}>
          Resources
        </Link>
        <a href="/app" className="lp-btn-primary" style={{ padding: '10px 22px', fontSize: 15 }}>Open App →</a>
      </div>
    </div>
  </nav>
);

const UrgencyBanner = () => (
  <div style={{ background: C.gold, padding: '11px 24px', textAlign: 'center' }}>
    <p style={{ fontSize: 14, fontWeight: 600, color: C.navyDark, letterSpacing: '0.02em' }}>
      ⚠️ HB 913 DEADLINE: Full reserve funding mandatory from January 1, 2026. DBPR enforcement is active.{' '}
      <a href="#pricing" style={{ color: C.navyDark, textDecoration: 'underline', fontWeight: 700 }}>Is your association compliant?</a>
    </p>
  </div>
);

const Hero = () => (
  <div style={{ background: `linear-gradient(160deg, ${C.navyDark} 0%, ${C.navyMid} 100%)`, padding: '96px 24px 104px', position: 'relative', overflow: 'hidden' }}>
    {/* Background texture */}
    <div style={{
      position: 'absolute', inset: 0, opacity: 0.04,
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px)',
      pointerEvents: 'none',
    }} />

    <div style={{ maxWidth: 820, margin: '0 auto', position: 'relative' }}>
      {/* Badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'rgba(42,157,143,0.18)', border: `1px solid rgba(42,157,143,0.4)`,
        borderRadius: 100, padding: '6px 16px', marginBottom: 32,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.teal, animation: 'pulse-badge 2s infinite' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: C.tealLight, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Florida SIRS Compliance Tool
        </span>
      </div>

      <h1 className="hero-headline" style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(36px, 5.5vw, 58px)',
        fontWeight: 800, color: C.white, lineHeight: 1.1,
        letterSpacing: '-0.02em', marginBottom: 28,
      }}>
        Turn your SIRS report into<br />
        <span style={{ color: C.teal }}>an HB 913 baseline funding plan</span><br />
        in minutes.
      </h1>

      <p style={{ fontSize: 20, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, marginBottom: 44, maxWidth: 620, fontWeight: 300 }}>
        Florida condo boards upload their engineering report. FloridaSIRS.org extracts all 8 mandated components and generates the 30-year baseline funding plan that proves your reserves never drop below zero — exactly as HB 913 requires.
      </p>

      <div className="hero-btns" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <a href="/app" className="lp-btn-primary" style={{ fontSize: 18, padding: '18px 36px' }}>
          Upload Your SIRS Report — Free →
        </a>
        <a href="#how-it-works" className="lp-btn-secondary" style={{ fontSize: 18, padding: '18px 36px' }}>
          See how it works
        </a>
      </div>

      <p style={{ marginTop: 20, fontSize: 13, color: 'rgba(255,255,255,0.38)', letterSpacing: '0.01em' }}>
        No account required to start. Free tier available.
      </p>
    </div>
  </div>
);

const Stats = () => {
  const ref = useFadeIn();
  return (
    <div style={{ background: C.navy, padding: '48px 24px' }}>
      <div ref={ref} className="fade-in" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="stats-row" style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0,
          borderTop: `1px solid rgba(255,255,255,0.1)`, borderLeft: `1px solid rgba(255,255,255,0.1)`,
        }}>
          {[
            { stat: '12,000+', label: 'Florida condo associations require a SIRS' },
            { stat: '6,000+', label: 'Estimated non-compliant as of early 2026' },
            { stat: '$35,000', label: 'Maximum cost of an engineering SIRS report' },
            { stat: '$29/mo', label: 'FloridaSIRS Pro — full HB 913 compliance tool' },
          ].map(({ stat, label }) => (
            <div key={stat} style={{
              padding: '32px 28px', textAlign: 'center',
              borderRight: `1px solid rgba(255,255,255,0.1)`, borderBottom: `1px solid rgba(255,255,255,0.1)`,
            }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 38, fontWeight: 700, color: C.teal, marginBottom: 8 }}>{stat}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, fontWeight: 300 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Problem = () => (
  <div style={{ background: C.offWhite, padding: '96px 24px' }}>
    <Section>
      <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>The Gap Nobody Has Filled</p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.navy, lineHeight: 1.2, marginBottom: 28 }}>
          Your engineering firm delivered the SIRS report.<br />Now what?
        </h2>
        <p style={{ fontSize: 17, color: C.slate, lineHeight: 1.7, marginBottom: 48 }}>
          You have a 30–50 page engineering PDF. You need to present a funding plan to your board next month. Assessments are going up 40%. Owners are going to ask hard questions. And the DBPR is actively enforcing HB 913 compliance.
        </p>
      </div>
      <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
        {[
          {
            icon: '📄',
            title: 'The engineering report is not the funding plan',
            body: 'Your SIRS report tells you what needs replacing and when. HB 913 requires a separate baseline funding plan proving reserves never drop below $0 over 30 years. Most boards don\'t know this distinction.',
          },
          {
            icon: '👥',
            title: 'Owners deserve a clear explanation',
            body: 'Assessment increases of 30–80% generate angry owners and contentious meetings. Boards that can show a clear visual funding roadmap — exactly why assessments are rising and what the alternative is — face far less pushback.',
          },
          {
            icon: '⚖️',
            title: 'Board members face personal liability',
            body: 'Under §718.112, failing to implement a compliant SIRS funding plan exposes board members to personal fiduciary liability. The DBPR now has 10 new investigators specifically for condo compliance.',
          },
        ].map(({ icon, title, body }) => (
          <div key={title} style={{ background: C.white, borderRadius: 8, padding: '36px 32px', border: `1px solid ${C.border}`, boxShadow: '0 2px 12px rgba(27,58,92,0.06)' }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>{icon}</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 12, lineHeight: 1.3 }}>{title}</h3>
            <p style={{ fontSize: 15, color: C.slate, lineHeight: 1.65 }}>{body}</p>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

const HowItWorks = () => (
  <div id="how-it-works" style={{ background: C.white, padding: '96px 24px' }}>
    <Section>
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Three Steps</p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.navy, lineHeight: 1.2 }}>
          From SIRS PDF to compliant funding plan
        </h2>
      </div>
      <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 48, position: 'relative' }}>
        {[
          {
            num: '01',
            title: 'Upload your SIRS report',
            body: 'Drag and drop your engineering PDF. Our AI extracts all 8 HB 913-mandated components — costs, remaining useful life, and replacement schedules — and displays them for your review and correction.',
          },
          {
            num: '02',
            title: 'Generate the baseline funding plan',
            body: 'One click produces a 30-year HB 913-compliant projection. The plan proves your reserve balance never drops below $0, calculates required monthly contributions per unit, and models special assessment scenarios.',
          },
          {
            num: '03',
            title: 'Share the funding roadmap',
            body: 'Export a visual PDF report your owners can actually understand. Show exactly why assessments are rising, what each dollar funds, and what happens if the community doesn\'t comply.',
          },
        ].map(({ num, title, body }, i) => (
          <div key={num} style={{ position: 'relative' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 4,
              background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 24,
            }}>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: C.teal }}>{num}</span>
            </div>
            {i < 2 && (
              <div style={{
                position: 'absolute', top: 28, left: 56, right: -48,
                height: 1, background: `linear-gradient(90deg, ${C.border} 0%, transparent 100%)`,
                display: 'block',
              }} className="step-connector" />
            )}
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: C.navy, marginBottom: 14, lineHeight: 1.3 }}>{title}</h3>
            <p style={{ fontSize: 15, color: C.slate, lineHeight: 1.7 }}>{body}</p>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

const Features = () => (
  <div style={{ background: C.offWhite, padding: '96px 24px' }}>
    <Section>
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>What FloridaSIRS.org Does</p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.navy, lineHeight: 1.2 }}>
          Everything a Florida condo board needs.<br />Nothing a general HOA tool provides.
        </h2>
      </div>
      <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
        {[
          { icon: '🤖', title: 'AI-powered SIRS extraction', body: 'Upload any engineering firm\'s PDF format. Our AI reads the document and pulls every component cost, remaining useful life, and replacement year — no manual data entry.' },
          { icon: '📊', title: 'HB 913 baseline funding calculator', body: 'Generates the specific plan Florida law requires: a 30-year projection where reserve balance never falls below $0. Calculates the minimum annual contribution to stay compliant.' },
          { icon: '🔀', title: 'Scenario modelling', body: 'Model monthly assessment increases, one-time special assessments, loans, and lines of credit side by side. HB 913 allows blended funding — see the 30-year impact of every option.' },
          { icon: '📑', title: 'Owner-facing PDF reports', body: 'Generate a plain-English report explaining why assessments are rising and what each dollar funds. Reduce contentious meetings with documentation owners can actually understand.' },
          { icon: '📐', title: 'Per-unit contribution breakdown', body: 'Automatically calculates the required monthly contribution per unit based on your association size. Shows owners exactly what their share is and why.' },
          { icon: '🛡️', title: 'Built for Florida SIRS — only', body: 'Not a generic HOA tool with a Florida checkbox. Every calculation, every field, and every report output is built specifically for HB 913 compliance and Florida §718.112.' },
        ].map(({ icon, title, body }) => (
          <div key={title} style={{
            background: C.white, borderRadius: 8, padding: '32px 28px',
            border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(27,58,92,0.05)',
            transition: 'box-shadow 0.2s, transform 0.2s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(27,58,92,0.12)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(27,58,92,0.05)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}
          >
            <div style={{ fontSize: 28, marginBottom: 14 }}>{icon}</div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: C.navy, marginBottom: 10, lineHeight: 1.35 }}>{title}</h3>
            <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.65 }}>{body}</p>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

const Pricing = () => (
  <div id="pricing" style={{ background: C.white, padding: '96px 24px' }}>
    <Section>
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Pricing</p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.navy, lineHeight: 1.2, marginBottom: 16 }}>
          Less than one hour of consultant time.
        </h2>
        <p style={{ fontSize: 17, color: C.slate, maxWidth: 520, margin: '0 auto' }}>
          A property management consultant charges $150–250/hour. A SIRS-related legal dispute costs $5,000–50,000. FloridaSIRS Pro costs $29/month.
        </p>
      </div>

      <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, maxWidth: 960, margin: '0 auto' }}>
        {/* Free */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '40px 32px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.slate, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Free</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 44, fontWeight: 700, color: C.navy, marginBottom: 4 }}>$0</div>
          <div style={{ fontSize: 14, color: C.slateLight, marginBottom: 32 }}>No credit card required</div>
          <a href="/app" className="lp-btn-primary" style={{ background: C.navy, width: '100%', justifyContent: 'center', display: 'flex', marginBottom: 32, fontSize: 15, padding: '13px 24px' }}>
            Get started free →
          </a>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Upload any SIRS PDF',
              'AI extracts all 8 components',
              'Preview first 3 components',
              '10-year projection summary',
              'See if you have a funding gap',
            ].map(f => (
              <li key={f} style={{ fontSize: 14, color: C.slate, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: C.teal, fontWeight: 700, marginTop: 1 }}>✓</span> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Pro */}
        <div style={{ border: `2px solid ${C.teal}`, borderRadius: 8, padding: '40px 32px', position: 'relative', boxShadow: '0 8px 40px rgba(42,157,143,0.15)' }}>
          <div style={{
            position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
            background: C.teal, color: C.white, fontSize: 12, fontWeight: 700,
            padding: '4px 16px', borderRadius: 100, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
          }}>Most popular</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Pro</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 44, fontWeight: 700, color: C.navy }}>$29</span>
            <span style={{ fontSize: 16, color: C.slate }}>/month</span>
          </div>
          <div style={{ fontSize: 14, color: C.slateLight, marginBottom: 32 }}>Billed monthly, cancel anytime</div>
          <a href="https://buy.stripe.com/dRmaEXb2LgVR7BF61U6Na00" className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center', display: 'flex', marginBottom: 32, fontSize: 15, padding: '13px 24px' }}>
            Start Pro →
          </a>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Everything in Free',
              'All 8 SIRS components unlocked',
              'Full 30-year HB 913 baseline plan',
              'Unlimited scenario modelling',
              'PDF export with association branding',
              'Owner-facing shareable report',
              'Per-unit contribution breakdown',
            ].map(f => (
              <li key={f} style={{ fontSize: 14, color: C.slate, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: C.teal, fontWeight: 700, marginTop: 1 }}>✓</span> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Audit-Ready */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '40px 32px', background: C.navyDark }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.tealLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Audit-Ready Package</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 44, fontWeight: 700, color: C.white }}>$299</span>
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginBottom: 32 }}>One-time payment</div>
          <a href="https://buy.stripe.com/00wcN5b2L6hd1dh0HA6Na01" target="_blank" rel="noreferrer" className="lp-btn-primary" style={{ background: C.teal, width: '100%', justifyContent: 'center', display: 'flex', marginBottom: 32, fontSize: 15, padding: '13px 24px' }}>
            Go Audit-Ready →
          </a>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Everything in Pro',
              'Full compliance document package',
              'Board affidavit template',
              'Owner notification templates',
              'DBPR reporting checklist',
              'One-time deliverable — keep forever',
            ].map(f => (
              <li key={f} style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: C.tealLight, fontWeight: 700, marginTop: 1 }}>✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState<number | null>(null);
  const faqs = [
    { q: 'Is FloridaSIRS.org affiliated with the DBPR or the State of Florida?', a: 'No. FloridaSIRS.org is a private SaaS tool. It is not affiliated with the Florida Department of Business and Professional Regulation or the Division of Condominiums, Timeshares and Mobile Homes.' },
    { q: 'What makes this different from a general HOA reserve calculator?', a: 'FloridaSIRS.org is built specifically for Florida HB 913 compliance. It generates the "baseline funding plan" format Florida law requires — a 30-year projection proving reserves never fall below $0. General HOA calculators do not produce this specific output.' },
    { q: 'Do I still need an engineering firm to produce my SIRS report?', a: 'Yes. Florida law requires your SIRS to be produced by a licensed engineer or architect. FloridaSIRS.org is not an engineering tool — it helps you manage, plan, and communicate the output of your engineering SIRS report.' },
    { q: 'What engineering report formats does the AI support?', a: 'Our AI can read any PDF-format SIRS report regardless of which engineering firm produced it. You always review and confirm the extracted data before generating your funding plan.' },
    { q: 'Is my SIRS data stored securely?', a: 'For the free tier, your data is processed locally in your browser and not stored on our servers. Pro tier data handling is described in our Privacy Policy.' },
    { q: 'Can the tool generate reports for the DBPR portal?', a: 'The current version generates HB 913 baseline funding plans and owner-facing reports. DBPR reporting integration is on our roadmap. Email hello@floridasirs.org to be notified when it launches.' },
  ];

  return (
    <div style={{ background: C.offWhite, padding: '96px 24px' }}>
      <Section>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>FAQ</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 700, color: C.navy }}>Common questions</h2>
        </div>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {faqs.map(({ q, a }, i) => (
            <div key={i} style={{ borderTop: `1px solid ${C.border}`, borderBottom: i === faqs.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: '100%', textAlign: 'left', padding: '22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'none', border: 'none', cursor: 'pointer', gap: 16,
              }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: C.navy, lineHeight: 1.4 }}>{q}</span>
                <span style={{ fontSize: 22, color: C.teal, flexShrink: 0, transform: open === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>+</span>
              </button>
              {open === i && (
                <p style={{ fontSize: 15, color: C.slate, lineHeight: 1.7, paddingBottom: 22 }}>{a}</p>
              )}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const FinalCTA = () => (
  <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, padding: '96px 24px', textAlign: 'center' }}>
    <Section>
      <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Get started today</p>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 700, color: C.white, lineHeight: 1.2, marginBottom: 24, maxWidth: 680, margin: '0 auto 24px' }}>
        Upload your SIRS report.<br />Have your baseline funding plan in minutes.
      </h2>
      <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.6)', marginBottom: 44, maxWidth: 480, margin: '0 auto 44px', fontWeight: 300, lineHeight: 1.6 }}>
        Free to start. No account required. The full 30-year HB 913 funding plan is one upload away.
      </p>
      <a href="/app" className="lp-btn-primary" style={{ fontSize: 19, padding: '20px 44px' }}>
        Upload Your SIRS Report — Free →
      </a>
    </Section>
  </div>
);

const Footer = () => (
  <footer style={{ background: C.navyDark, padding: '48px 24px 32px', borderTop: `2px solid ${C.teal}` }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 32, marginBottom: 40 }}>
        <div style={{ maxWidth: 360 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <img src="/logo.png" alt="FloridaSIRS" style={{ height: 36 }} />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: C.white }}>
              FloridaSIRS<span style={{ color: C.teal }}>.org</span>
            </span>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
            Florida SIRS compliance tool for condo boards and property managers. Built specifically for HB 913 baseline funding plan requirements.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 64, flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.teal, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Product</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[['How it works', '#how-it-works'], ['Pricing', '#pricing'], ['Open App', '/app']].map(([label, href]) => (
                <a key={label} href={href} style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>{label}</a>
              ))}
            </div>
          </div>
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.teal, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Legal</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[['Privacy Policy', '/privacy'], ['Contact', 'mailto:hello@floridasirs.org']].map(([label, href]) => (
                <a key={label} href={href} style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>{label}</a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: `1px solid rgba(255,255,255,0.08)`, paddingTop: 28 }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, marginBottom: 12 }}>
          FloridaSIRS.org generates financial projections based on data from your Structural Integrity Reserve Study. It does not constitute legal, engineering, or financial advice. Consult your licensed professionals for compliance determinations.
        </p>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>
          FloridaSIRS.org is not affiliated with the Florida Department of Business and Professional Regulation (DBPR) or the Division of Condominiums, Timeshares and Mobile Homes. © {new Date().getFullYear()} FloridaSIRS.org. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <>
      <FontLink />
      <Navbar />
      <UrgencyBanner />
      <Hero />
      <Stats />
      <Problem />
      <HowItWorks />
      <Features />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </>
  );
}
